import { getConnectedRequestProfilePathname } from '../../utils/link'
import {
  FILE_UPLOAD_API_ERROR_TRANSLATION_KEY,
  getCheckedFilesListByChunk,
} from '../File'
import {
  REQUEST_FORM_FIELD_NAME_APPLICANT,
  REQUEST_FORM_INITIAL_VALUES,
} from '../Request'
import { toUserWithPropertyInfoSelectItemsFromOption } from '../User'

import {
  getIdwellToIdwellFileDocumentById,
  uploadIdwellToIdwellFileDocument,
} from './IdwellToIdwell.api'
import { IDWELL_TO_IDWELL_FILE_POLL_CONFIG } from './IdwellToIdwell.constants'
import { IDWELL_TO_IDWELL_OUTBOUND_COMPANY_ACTIVE_STATUS } from './IdwellToIdwellSubMenu'

function getIdwellToIdwellFileApi(outboundUuid) {
  return {
    async fetchById(id) {
      const { data } = await getIdwellToIdwellFileDocumentById({
        id,
        outboundUuid,
      })

      return data
    },

    async upload(file) {
      const res = await uploadIdwellToIdwellFileDocument(file, outboundUuid)
      const id = res?.id ?? res?.data?.id

      return { checking: true, id, name: file.name, size: file.size }
    },
  }
}

function toUploadError(file, err) {
  return {
    ...file,
    error: {
      isPersistent: true,
      text:
        FILE_UPLOAD_API_ERROR_TRANSLATION_KEY[
          err?.response?.data?.errors?.origin
        ] || 'Error.UploadingFileApiErrorKey',
    },
    uploading: true,
  }
}

async function uploadSafe(file, api) {
  try {
    return await api.upload(file)
  } catch (err) {
    return toUploadError(file, err)
  }
}

async function pollFile(file, api) {
  let fileData = await uploadSafe(file, api)
  let attempt = 0
  let delay = IDWELL_TO_IDWELL_FILE_POLL_CONFIG.shortDelayMs

  while (fileData.checking || fileData.message) {
    attempt++

    fileData = await new Promise((resolve, reject) =>
      setTimeout(() => {
        api
          .fetchById(fileData.id)
          .then(resolve)
          .catch(err => reject(toUploadError(file, err)))
      }, delay)
    )

    if (attempt >= IDWELL_TO_IDWELL_FILE_POLL_CONFIG.attemptsToIncreaseDelay) {
      delay = IDWELL_TO_IDWELL_FILE_POLL_CONFIG.longDelayMs
    }

    if (attempt === IDWELL_TO_IDWELL_FILE_POLL_CONFIG.maxAttempts) {
      break
    }
  }

  return fileData
}

async function getCheckedFileList(files, api) {
  return Promise.all(files.map(file => pollFile(file, api)))
}

function getIdwellToIdwellFilesCheckedList(files, api) {
  return getCheckedFilesListByChunk({
    files,
    getFilesList: ({ files }) => getCheckedFileList(files, api),
  })
}

export function getIdwellToIdwellFiles(outboundUuid) {
  const api = getIdwellToIdwellFileApi(outboundUuid)

  return function getFiles({ files }) {
    return getIdwellToIdwellFilesCheckedList(files, api)
  }
}

export function getIdwellToIdwellServiceRequestCreationInitialValues({
  boundUser,
}) {
  const [preparedInitialUser] = toUserWithPropertyInfoSelectItemsFromOption([
    boundUser,
  ])

  return {
    ...REQUEST_FORM_INITIAL_VALUES,
    [REQUEST_FORM_FIELD_NAME_APPLICANT]: preparedInitialUser,
  }
}

export function getIdwellToIdwellRequestFormSuccessNotification({
  availableSections,
  data,
  outboundUuid,
  t,
}) {
  return {
    linkProps: {
      reloadDocument: true,
      title: t('Notification.RequestCreatedViewRequestLinkKey'),
      to: getConnectedRequestProfilePathname({
        availableSections,
        id: data.id,
        outboundUuid,
      }),
    },
    text: t('Notification.RequestCreatedTextKey', {
      requestNumber: data.request_no,
      title: data.title,
    }),
    title: t('Notification.RequestCreatedTitleKey'),
  }
}

export function isOutboundCompanyValid({ activeKey, company }) {
  return (
    company[activeKey]?.toLowerCase() ===
    IDWELL_TO_IDWELL_OUTBOUND_COMPANY_ACTIVE_STATUS
  )
}
